import React, { useState, useEffect } from 'react';
import { auth, googleProvider, githubProvider } from './auth-context';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

const SignIn = ({ isOpen, onClose }) => {
    const [authError, setAuthError] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    const handleGoogleSignIn = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
            setAuthError('');
            onClose();
        } catch (error) {
            setAuthError('Sign in failed. Please try again.');
            console.error('Sign in error:', error);
        }
    };

    const handleGitHubSignIn = async () => {
        try {
            await signInWithPopup(auth, githubProvider);
            setAuthError('');
            onClose();
        } catch (error) {
            if (error.code === 'auth/account-exists-with-different-credential') {
                setAuthError('An account already exists with this email. Try signing in with Google instead.');
            } else {
                setAuthError('Sign in failed. Please try again.');
            }
            console.error('Sign in error:', error);
        }
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setAuthError('Please enter both email and password.');
            return;
        }
        setEmailLoading(true);
        setAuthError('');

        try {
            if (isSignUp) {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            setEmail('');
            setPassword('');
            setAuthError('');
            onClose();
        } catch (error) {
            if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
                setAuthError('No account found with this email. Try signing up instead.');
            } else if (error.code === 'auth/wrong-password') {
                setAuthError('Incorrect password. Please try again.');
            } else if (error.code === 'auth/email-already-in-use') {
                setAuthError('An account with this email already exists. Try signing in.');
            } else if (error.code === 'auth/weak-password') {
                setAuthError('Password must be at least 16 characters with an uppercase letter, number, and special character.');
            } else if (error.code === 'auth/invalid-email') {
                setAuthError('Please enter a valid email address.');
            } else {
                setAuthError('Authentication failed. Please try again.');
            }
            console.error('Email auth error:', error);
        } finally {
            setEmailLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full px-4">
                <div className="rounded-2xl bg-[#0B1224] ring-1 ring-white/[0.12] shadow-[0_0_60px_-15px_rgba(6,182,212,0.15),inset_0_1px_0_0_rgba(255,255,255,0.1)] p-8 max-w-md w-full mx-auto">
                    <h2 className="text-3xl font-bold text-center text-blue-400 mb-4">
                        {isSignUp ? 'Create Account' : 'Sign In'}
                    </h2>
                    {authError && (
                        <div className="bg-red-900/50 ring-1 ring-red-500/30 text-white p-4 rounded-xl mb-6 text-sm">
                            {authError}
                        </div>
                    )}
                    <div className="space-y-4">
                        <p className="text-white/70 text-center text-sm mb-2">
                            {isSignUp
                                ? 'Create an account to track your progress.'
                                : 'Sign in to access practice tests, flashcards, and track your progress.'
                            }
                        </p>

                        {/* Email/Password Form */}
                        <form onSubmit={handleEmailAuth} className="space-y-3">
                            <input
                                type="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-cyan-500/50 transition-colors"
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-white/5 ring-1 ring-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-cyan-500/50 transition-colors"
                            />
                            {isSignUp && (
                                <center><div className="text-xs text-white/70 space-y-1 px-1">
                                    <p className="text-white/70 font-medium">Password must contain:</p>
                                    <ul className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                        <li className={password.length >= 16 ? 'text-green-400' : ''}>
                                            {password.length >= 16 ? '✓' : '·'} 16+ characters
                                        </li>
                                        <li className={/[A-Z]/.test(password) ? 'text-green-400' : ''}>
                                            {/[A-Z]/.test(password) ? '✓' : '·'} Uppercase letter
                                        </li>
                                        <li className={/[0-9]/.test(password) ? 'text-green-400' : ''}>
                                            {/[0-9]/.test(password) ? '✓' : '·'} 1+ numbers&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;    
                                        </li>
                                        <li className={/[^A-Za-z0-9]/.test(password) ? 'text-green-400' : ''}>
                                            {/[^A-Za-z0-9]/.test(password) ? '✓' : '·'} Special character
                                        </li>
                                    </ul>
                                </div></center>
                            )}
                            <button
                                type="submit"
                                disabled={emailLoading}
                                className={`w-full flex items-center justify-center bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-medium py-3 px-4 rounded-xl transition duration-150 ${emailLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {emailLoading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
                            </button>
                        </form>

                        <button
                            onClick={() => { setIsSignUp(!isSignUp); setAuthError(''); }}
                            className="w-full text-center text-sm text-white/70/70 hover:text-cyan-400 transition-colors"
                        >
                            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                        </button>

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-white/10" />
                            <span className="text-xs text-white/35">or</span>
                            <div className="flex-1 h-px bg-white/10" />
                        </div>

                        {/* OAuth buttons */}
                        <button
                            onClick={handleGoogleSignIn}
                            className="w-full flex items-center justify-center bg-white/[0.08] hover:bg-white/[0.14] ring-1 ring-white/10 text-white font-medium py-3 px-4 rounded-xl transition duration-150 backdrop-blur"
                        >
                            <img
                                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                alt="Google logo"
                                className="w-5 h-5 mr-2"
                            />
                            Continue with Google
                        </button>
                        <button
                            onClick={handleGitHubSignIn}
                            className="w-full flex items-center justify-center bg-white/[0.05] hover:bg-white/[0.1] ring-1 ring-white/10 text-white font-medium py-3 px-4 rounded-xl transition duration-150 backdrop-blur"
                        >
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                            Continue with GitHub
                        </button>
                        <div className="text-white/45 text-xs text-center mt-2">
                            By signing in, you agree to our Terms of Service & Privacy Policy
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignIn;
